import React from 'react';
import { Modal, View, Text, StyleSheet, Image, TouchableOpacity, ScrollView } from 'react-native';
import type { ReceiptData } from './ReceiptCard';
import { MODULE_COLORS } from '@cubemeet/core';

export interface SplitMember {
  user_id: string;
  amount?: number | null;
  percentage?: number | null;
  share?: number | null;
  note?: string | null;
  profile?: { display_name?: string | null; avatar_url?: string | null } | null;
}

export interface ReceiptSplitDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  receipt: (ReceiptData & { members?: SplitMember[] }) | null;
  currentUserId?: string | null;
  formatCurrency?: (amount: number, currency?: string) => string;
}

const ReceiptSplitDetailsModal: React.FC<ReceiptSplitDetailsModalProps> = ({ visible, onClose, receipt, currentUserId, formatCurrency }) => {
  if (!receipt) return null;

  const members = (receipt as any).members as SplitMember[] | undefined;

  const totalFromMembers = members && members.length ? members.reduce((s, m) => s + (Number(m.amount) || 0), 0) : null;

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <Text style={styles.title}>{receipt.title || 'Receipt details'}</Text>
            <Text style={styles.total}>{formatCurrency ? formatCurrency(receipt.amount, receipt.currency) : `${receipt.currency}${receipt.amount}`}</Text>
          </View>

          <ScrollView style={styles.body}>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Split mode</Text>
              <Text style={styles.sectionValue}>{(receipt as any).split_mode || 'unknown'}</Text>
            </View>

            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Participants</Text>
              {(!members || members.length === 0) && (
                <Text style={styles.empty}>No participant breakdown available.</Text>
              )}

              {members && members.map((m) => (
                <View key={m.user_id} style={[styles.memberRow, currentUserId === m.user_id ? styles.highlight : undefined]}>
                  <View style={styles.memberLeft}>
                    {m.profile && m.profile.avatar_url ? (
                      <Image source={{ uri: m.profile.avatar_url }} style={styles.avatar} />
                    ) : (
                      <View style={styles.avatarPlaceholder}><Text style={styles.avatarLetter}>{(m.profile?.display_name || m.user_id || '?').charAt(0).toUpperCase()}</Text></View>
                    )}
                    <View style={styles.memberInfo}>
                      <Text style={styles.memberName}>{m.profile?.display_name || m.user_id}</Text>
                      {m.note ? <Text style={styles.memberNote}>{m.note}</Text> : null}
                    </View>
                  </View>
                  <View style={styles.memberRight}>
                    <Text style={styles.memberAmount}>{m.amount != null ? (formatCurrency ? formatCurrency(Number(m.amount), receipt.currency) : `${receipt.currency}${Number(m.amount)}`) : '-'}</Text>
                    {m.percentage != null ? <Text style={styles.memberMeta}>{m.percentage}%</Text> : null}
                    {m.share != null ? <Text style={styles.memberMeta}>share: {m.share}</Text> : null}
                  </View>
                </View>
              ))}

              {totalFromMembers != null && Math.abs(totalFromMembers - (Number(receipt.amount) || 0)) > 0.005 && (
                <Text style={styles.roundingNote}>Note: member sums ({totalFromMembers.toFixed(2)}) differ from receipt total due to rounding.</Text>
              )}
            </View>
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}><Text style={styles.closeText}>Close</Text></TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: '#fff', borderTopLeftRadius: 12, borderTopRightRadius: 12, maxHeight: '80%' },
  header: { padding: 16, borderBottomWidth: 1, borderColor: '#f1f5f9', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: 16, fontWeight: '700', color: '#111827' },
  total: { fontSize: 16, fontWeight: '700', color: MODULE_COLORS.travel },
  body: { paddingHorizontal: 12, paddingVertical: 8 },
  section: { marginBottom: 12 },
  sectionTitle: { fontSize: 13, color: '#6b7280', marginBottom: 6 },
  sectionValue: { fontSize: 14, color: '#111827', fontWeight: '600' },
  empty: { color: '#9ca3af' },
  memberRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderColor: '#f1f5f9' },
  highlight: { backgroundColor: '#fffaf0' },
  memberLeft: { flexDirection: 'row', alignItems: 'center' },
  avatar: { width: 40, height: 40, borderRadius: 20, marginRight: 10 },
  avatarPlaceholder: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#eef2ff', alignItems: 'center', justifyContent: 'center', marginRight: 10 },
  avatarLetter: { color: MODULE_COLORS.travel, fontWeight: '700' },
  memberInfo: { maxWidth: 180 },
  memberName: { fontSize: 14, fontWeight: '600', color: '#111827' },
  memberNote: { fontSize: 12, color: '#9ca3af' },
  memberRight: { alignItems: 'flex-end' },
  memberAmount: { fontSize: 14, fontWeight: '700', color: '#111827' },
  memberMeta: { fontSize: 12, color: '#9ca3af' },
  roundingNote: { fontSize: 12, color: '#9ca3af', marginTop: 8 },
  footer: { padding: 12, borderTopWidth: 1, borderColor: '#f1f5f9', alignItems: 'flex-end' },
  closeButton: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, backgroundColor: '#f8fafc' },
  closeText: { color: '#111827', fontWeight: '600' },
});

export default ReceiptSplitDetailsModal;
